import { useCallback, useEffect, useState } from 'react'

export type TNavState = {
  /** Desktop: label of the menu whose panel is open. */
  openId: string | null
  /** Mobile: whether the expanded nav is open. */
  mobileOpen: boolean
  /**
   * Mobile: the screens pushed over the root list, outermost first —
   * `[menuLabel]` on a menu's screen, `[menuLabel, groupHeading]` on a
   * group's screen.
   */
  mobileStack: string[]
  isCoarsePointer: boolean
  openMenu: (id: string) => void
  closeMenu: () => void
  toggleMobile: () => void
  pushMobile: (id: string) => void
  popMobile: () => void
  closeMobile: () => void
}

function detectCoarsePointer(): boolean {
  if (typeof globalThis.window === 'undefined') return false
  const mm = (
    globalThis.window as { matchMedia?: (q: string) => { matches: boolean } }
  ).matchMedia
  if (typeof mm !== 'function') return false
  return mm('(pointer: coarse)').matches
}

export function useNavState(): TNavState {
  const [openId, setOpenId] = useState<string | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileStack, setMobileStack] = useState<string[]>([])
  const [isCoarsePointer, setIsCoarsePointer] = useState(false)

  useEffect(() => {
    setIsCoarsePointer(detectCoarsePointer())
  }, [])

  const openMenu = useCallback((id: string) => {
    setOpenId((current) => (current === id ? current : id))
  }, [])

  const closeMenu = useCallback(() => {
    setOpenId(null)
  }, [])

  // Opening always starts at the root; closing forgets the stack so the
  // next open starts fresh, like apple.com's flyout.
  const toggleMobile = useCallback(() => {
    setMobileOpen((prev) => !prev)
    setMobileStack([])
  }, [])

  const pushMobile = useCallback((id: string) => {
    setMobileStack((prev) => [...prev, id])
  }, [])

  const popMobile = useCallback(() => {
    setMobileStack((prev) => prev.slice(0, -1))
  }, [])

  const closeMobile = useCallback(() => {
    setMobileOpen(false)
    setMobileStack([])
  }, [])

  return {
    openId,
    mobileOpen,
    mobileStack,
    isCoarsePointer,
    openMenu,
    closeMenu,
    toggleMobile,
    pushMobile,
    popMobile,
    closeMobile,
  }
}
